import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { colors } from '../styles/tokens'
import { Button, Input, Select } from '../components/FormControls'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from '../components/Feedback'
import { parseCostCodeFile, validateCostCodeRows, downloadCostCodeTemplate } from '../lib/costCodeResourceImport'

// =======================================================================
// Cost Code Dictionary — manages estimation.canonical_cost_code, the
// single existing source of truth already consumed directly by BOQ,
// Budget, Indirect Cost, Risk/Contingency, and HO Overhead line items
// via canonical_cost_code_id. This page performs ZERO financial
// calculation and does not introduce a second Cost Code table.
//
// Write access (create/edit) is enforced server-side by RLS
// (is_platform_admin()), added in the prior Batch A migration. This
// page does not attempt to reproduce that check client-side — every
// authenticated user can view the Dictionary; only a platform admin's
// INSERT/UPDATE will actually succeed against the database, and any
// rejection is surfaced verbatim, matching the existing pattern used
// throughout Estimation (see FirstPrinciplesLibraryPage.jsx).
//
// costspace_matched_code is a manual, free-text reference field only.
// No CostSpace sync/matching logic exists or is implemented here.
// =======================================================================

export default function CostCodeDictionaryPage() {
  const { tenderId } = useParams()
  const [codes, setCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(false)

  // Create form state
  const [newCode, setNewCode] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newDescriptionAr, setNewDescriptionAr] = useState('')
  const [newParentId, setNewParentId] = useState('')
  const [newCostspaceMatched, setNewCostspaceMatched] = useState('')
  const [creating, setCreating] = useState(false)

  // Edit-in-place state
  const [editingId, setEditingId] = useState(null)
  const [editCode, setEditCode] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editDescriptionAr, setEditDescriptionAr] = useState('')
  const [editParentId, setEditParentId] = useState('')
  const [editCostspaceMatched, setEditCostspaceMatched] = useState('')
  const [savingId, setSavingId] = useState(null)

  // Import
  const [showImport, setShowImport] = useState(false)
  const [importRows, setImportRows] = useState(null)
  const [importParseError, setImportParseError] = useState(null)
  const [importing, setImporting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error } = await supabase
      .from('canonical_cost_code')
      .select('id, code, description, description_ar, parent_id, level, is_active, costspace_matched_code')
      .eq('tender_id', tenderId)
      .order('code')
    if (error) setError(error.message)
    else setCodes(data || [])
    setLoading(false)
  }, [tenderId])

  useEffect(() => { setCodes([]); load() }, [tenderId, load])

  // Build a stable_ref value from the code itself — canonical_cost_code
  // requires stable_ref NOT NULL; it is not user-facing/editable here.
  function slugify(code) {
    return (code || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  }

  async function handleCreate(e) {
    e.preventDefault()
    setError(null); setSuccessMsg(null)
    if (!newCode.trim() || !newDescription.trim()) {
      setError('Code and Description are required.')
      return
    }
    setCreating(true)
    // Tender-scoped RPC for the core create (code/description/parent/
    // level/stable_ref) -- create_cost_code() validates that any parent
    // belongs to this SAME tender server-side, rejecting a cross-tender
    // parent reference outright. description_ar and
    // costspace_matched_code are not part of the RPC's contract (free-
    // text secondary fields), so they're applied as a follow-up UPDATE
    // on the row the RPC just created -- RLS (tender_id match) protects
    // this update exactly like any other edit on an existing row.
    const { data: newId, error } = await supabase.rpc('create_cost_code', {
      p_tender_id: tenderId,
      p_code: newCode.trim(),
      p_description: newDescription.trim(),
      p_parent_id: newParentId || null,
    })
    if (error) {
      setError(`Could not create Cost Code — ${error.message}`)
      setCreating(false)
      return
    }
    if (newDescriptionAr.trim() || newCostspaceMatched.trim()) {
      await supabase.from('canonical_cost_code').update({
        description_ar: newDescriptionAr.trim() || null,
        costspace_matched_code: newCostspaceMatched.trim() || null,
      }).eq('id', newId)
    }
    setSuccessMsg('Cost Code created.')
    setNewCode(''); setNewDescription(''); setNewDescriptionAr('')
    setNewParentId(''); setNewCostspaceMatched('')
    await load()
    setCreating(false)
  }

  function startEdit(c) {
    setEditingId(c.id)
    setEditCode(c.code || '')
    setEditDescription(c.description || '')
    setEditDescriptionAr(c.description_ar || '')
    setEditParentId(c.parent_id || '')
    setEditCostspaceMatched(c.costspace_matched_code || '')
    setError(null); setSuccessMsg(null)
  }

  function cancelEdit() {
    setEditingId(null)
  }

  async function handleSaveEdit(id) {
    setError(null); setSuccessMsg(null)
    if (!editCode.trim() || !editDescription.trim()) {
      setError('Code and Description are required.')
      return
    }
    setSavingId(id)
    const { error } = await supabase.from('canonical_cost_code').update({
      code: editCode.trim(),
      description: editDescription.trim(),
      description_ar: editDescriptionAr.trim() || null,
      parent_id: editParentId || null,
      costspace_matched_code: editCostspaceMatched.trim() || null,
    }).eq('id', id)
    if (error) {
      setError(`Could not update Cost Code — ${error.message}`)
    } else {
      setSuccessMsg('Cost Code updated.')
      setEditingId(null)
      await load()
    }
    setSavingId(null)
  }

  async function handleToggleActive(c) {
    setError(null); setSuccessMsg(null)
    setSavingId(c.id)
    const { error } = await supabase.from('canonical_cost_code')
      .update({ is_active: !c.is_active }).eq('id', c.id)
    if (error) {
      setError(`Could not ${c.is_active ? 'deactivate' : 'reactivate'} Cost Code — ${error.message}`)
    } else {
      setSuccessMsg(c.is_active ? 'Cost Code deactivated.' : 'Cost Code reactivated.')
      await load()
    }
    setSavingId(null)
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setImportParseError(null)
    setImportRows(null)
    try {
      const rows = await parseCostCodeFile(file)
      setImportRows(validateCostCodeRows(rows))
    } catch (err) {
      setImportParseError(err.message)
    }
    e.target.value = ''
  }

  async function handleConfirmImport() {
    if (!importRows) return
    setImporting(true)
    setError(null); setSuccessMsg(null)
    const payload = importRows.map((r) => ({
      code: r.code, description: r.description,
      description_ar: r.description_ar || null,
      parent_code: r.parent_code || null,
      costspace_matched_code: r.costspace_matched_code || null,
    }))
    const { data, error } = await supabase.rpc('import_canonical_cost_codes', { p_tender_id: tenderId, p_rows: payload })
    if (error) {
      setError(`Import failed — ${error.message}`)
    } else {
      setSuccessMsg(`Imported ${data.inserted_count} Cost Code(s).`)
      setShowImport(false)
      setImportRows(null)
      await load()
    }
    setImporting(false)
  }

  const codeById = useMemo(() => Object.fromEntries(codes.map((c) => [c.id, c])), [codes])

  const visibleCodes = useMemo(() => {
    const term = search.trim().toLowerCase()
    return codes
      .filter((c) => showInactive || c.is_active)
      .filter((c) => !term || c.code.toLowerCase().includes(term) || (c.description || '').toLowerCase().includes(term))
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
  }, [codes, search, showInactive])

  // Parent options for the dropdowns exclude the row itself when editing,
  // to avoid a code becoming its own ancestor.
  function parentOptions(excludeId) {
    return codes.filter((c) => c.is_active && c.id !== excludeId)
  }

  return (
    <div>
      <Link to={`/estimation/tender/${tenderId}`} style={{ fontSize: 13, color: colors.textSecondary, textDecoration: 'none' }}>
        &larr; Back to Tender
      </Link>
      <div style={{ marginBottom: 16, marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: colors.ink }}>Cost Code Dictionary</div>
          <div style={{ fontSize: 13, color: colors.textSecondary }}>
            This Tender's Cost Code master used across BOQ, Budget, Indirect Costs, Risk/Contingency and HO Overhead.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={downloadCostCodeTemplate}>Download Template</Button>
          <Button variant="secondary" onClick={() => setShowImport((s) => !s)}>
            {showImport ? 'Cancel Import' : 'Import from Excel'}
          </Button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
      {successMsg && <SuccessBanner message={successMsg} />}

      {showImport && (
        <div style={{ padding: 16, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 20, background: colors.surfaceAlt }}>
          <input type="file" accept=".xlsx" onChange={handleImportFile} style={{ marginBottom: 12 }} />
          {importParseError && <ErrorBanner message={importParseError} />}
          {importRows && (
            <>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                Total: {importRows.length} | Valid: {importRows.filter((r) => r.status === 'valid').length} | Errors: {importRows.filter((r) => r.status === 'error').length}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.border2}`, textAlign: 'left' }}>
                    <th style={{ padding: 4 }}>Row</th><th style={{ padding: 4 }}>Code</th><th style={{ padding: 4 }}>Status</th><th style={{ padding: 4 }}>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.map((r) => (
                    <tr key={r.rowNumber} style={{ borderBottom: `1px solid ${colors.border}`, color: r.status === 'error' ? '#B91C1C' : colors.ink }}>
                      <td style={{ padding: 4 }}>{r.rowNumber}</td>
                      <td style={{ padding: 4 }}>{r.code || '—'}</td>
                      <td style={{ padding: 4 }}>{r.status}</td>
                      <td style={{ padding: 4 }}>{r.issues.join(' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button onClick={handleConfirmImport}
                disabled={importing || importRows.length === 0 || importRows.some((r) => r.status === 'error')}>
                {importing ? 'Importing…' : `Confirm Import (${importRows.filter((r) => r.status === 'valid').length})`}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Create form */}
      <form onSubmit={handleCreate} style={{
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end',
        padding: 16, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 20, background: colors.surfaceAlt,
      }}>
        <div>
          <label style={{ fontSize: 12, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Code *</label>
          <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="e.g. 03 20 00" style={{ width: 140 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Description *</label>
          <Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="e.g. Concrete Reinforcement" style={{ width: 240 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Description (Arabic)</label>
          <Input value={newDescriptionAr} onChange={(e) => setNewDescriptionAr(e.target.value)} dir="rtl" style={{ width: 200 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Parent</label>
          <Select value={newParentId} onChange={(e) => setNewParentId(e.target.value)} style={{ width: 220 }}>
            <option value="">— No parent (top level) —</option>
            {parentOptions(null).map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.description}</option>
            ))}
          </Select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>CostSpace Code (reference)</label>
          <Input value={newCostspaceMatched} onChange={(e) => setNewCostspaceMatched(e.target.value)} placeholder="Optional" style={{ width: 160 }} />
        </div>
        <Button type="submit" disabled={creating}>{creating ? 'Creating…' : '+ Add Cost Code'}</Button>
      </form>

      {/* Search / filter */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code or description…" style={{ width: 280 }} />
        <label style={{ fontSize: 13, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show deactivated
        </label>
        <div style={{ fontSize: 12, color: colors.textSecondary, marginLeft: 'auto' }}>{visibleCodes.length} code(s)</div>
      </div>

      {loading ? (
        <SkeletonRows rows={6} cols={6} />
      ) : visibleCodes.length === 0 ? (
        <EmptyState title="No Cost Codes found" description="Create the first Cost Code using the form above, or adjust your search." />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${colors.border2}`, textAlign: 'left' }}>
              <th style={{ padding: '8px 6px' }}>Code</th>
              <th style={{ padding: '8px 6px' }}>Description</th>
              <th style={{ padding: '8px 6px' }}>Parent</th>
              <th style={{ padding: '8px 6px' }}>CostSpace Code</th>
              <th style={{ padding: '8px 6px' }}>Status</th>
              <th style={{ padding: '8px 6px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleCodes.map((c) => {
              const isEditing = editingId === c.id
              const parent = c.parent_id ? codeById[c.parent_id] : null
              return (
                <tr key={c.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: c.is_active ? 1 : 0.55 }}>
                  {isEditing ? (
                    <>
                      <td style={{ padding: '6px' }}><Input value={editCode} onChange={(e) => setEditCode(e.target.value)} style={{ width: 120 }} /></td>
                      <td style={{ padding: '6px' }}><Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} style={{ width: 220 }} /></td>
                      <td style={{ padding: '6px' }}>
                        <Select value={editParentId} onChange={(e) => setEditParentId(e.target.value)} style={{ width: 200 }}>
                          <option value="">— No parent —</option>
                          {parentOptions(c.id).map((p) => (
                            <option key={p.id} value={p.id}>{p.code} — {p.description}</option>
                          ))}
                        </Select>
                      </td>
                      <td style={{ padding: '6px' }}><Input value={editCostspaceMatched} onChange={(e) => setEditCostspaceMatched(e.target.value)} style={{ width: 140 }} /></td>
                      <td style={{ padding: '6px' }}>{c.is_active ? 'Active' : 'Inactive'}</td>
                      <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Button size="sm" variant="secondary" onClick={cancelEdit} disabled={savingId === c.id}>Cancel</Button>{' '}
                        <Button size="sm" onClick={() => handleSaveEdit(c.id)} disabled={savingId === c.id}>
                          {savingId === c.id ? 'Saving…' : 'Save'}
                        </Button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '8px 6px', fontFamily: 'monospace' }}>
                        {'—'.repeat(Math.max(0, (c.level || 1) - 1))} {c.code}
                      </td>
                      <td style={{ padding: '8px 6px' }}>{c.description}{c.description_ar ? <span style={{ color: colors.textSecondary }}> ({c.description_ar})</span> : null}</td>
                      <td style={{ padding: '8px 6px', color: colors.textSecondary }}>{parent ? `${parent.code} — ${parent.description}` : '—'}</td>
                      <td style={{ padding: '8px 6px', color: colors.textSecondary }}>{c.costspace_matched_code || '—'}</td>
                      <td style={{ padding: '8px 6px' }}>{c.is_active ? 'Active' : 'Inactive'}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Button size="sm" variant="secondary" onClick={() => startEdit(c)}>Edit</Button>{' '}
                        <Button size="sm" variant="secondary" onClick={() => handleToggleActive(c)} disabled={savingId === c.id}>
                          {savingId === c.id ? '…' : c.is_active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
